import { ModalTreePickerElement } from 'components/molecules';
import React, { useContext, useMemo } from 'react';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import { AppFormContext } from 'contexts/AppFormContext';
import { CoreCurriculumDTO, CoreCurriculumName } from 'types';
import { formFields } from 'consts/form/formFields';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import { AppFormItem } from 'components/atoms';
import { useWatch } from 'antd/es/form/Form';
import { useDisplayFetchDataError } from 'hooks/useDisplayFetchDataError/useDisplayFetchDataError';
import { journalTexts } from 'consts/text';
import { IconPlus } from '@tabler/icons-react';
import { AppCustomSelect } from 'components/atoms/CommonAppComponents/AppSelect/AppCustomSelect/AppCustomSelect';
import {
    mapCoreCurriculumListToCoreCurriculumNamesList,
    mapCoreCurriculumListToSelectOptionsList,
    parseCoreCurriculumDataToTreeNodes,
} from './utils';
import { useToggle } from '../../../../hooks/useToggle/useToggle';

type TopicCoreCurriculumPickerProps = {
    lessonDate?: string;
};

export const TopicCoreCurriculumPicker = ({ lessonDate }: TopicCoreCurriculumPickerProps) => {
    const { setFieldValue } = useFormInstance();
    const { formError, setFormError } = useContext(AppFormContext);

    const [isModalOpen, openModal, closeModal] = useToggle(false);

    const curriculumsSelectValue =
        useWatch<CoreCurriculumName[] | undefined>(formFields.coreCurriculums) || [];

    const {
        data: coreCurriculumData,
        isFetching: isCoreCurriculumDataFetching,
        isError: isCoreCurriculumDataError,
    } = useAppQuery(
        'CORE_CURRICULUM',
        [lessonDate],
        () => StaffHTTPService.coreCurriculums.getCoreCurriculums(lessonDate as string),
        { enabled: !!lessonDate, staleTime: Infinity },
    );

    useDisplayFetchDataError({
        isFetching: isCoreCurriculumDataFetching,
        isError: isCoreCurriculumDataError,
        actualError: formError,
        errorMessage:
            journalTexts.journalDetails.groupJournal.topics.fetchingCoreCurriculumDataError,
        setError: setFormError,
    });

    const parsedCoreCurriculumData: CoreCurriculumDTO[] = useMemo(
        () => coreCurriculumData || [],
        [coreCurriculumData],
    );

    const onClearHandler = () => {
        setFieldValue(formFields.coreCurriculums, null);
    };

    const onSaveHandler = (values: React.Key[]) => {
        const coreCurriculumNames: React.Key[] =
            mapCoreCurriculumListToCoreCurriculumNamesList(parsedCoreCurriculumData);
        const filteredSelectedValues = values.filter((value) =>
            coreCurriculumNames.includes(value),
        );

        setFieldValue(formFields.coreCurriculums, filteredSelectedValues);
        closeModal();
    };

    const mappedCoreCurriculumDataForTree = useMemo(
        () => parseCoreCurriculumDataToTreeNodes(parsedCoreCurriculumData),
        [parsedCoreCurriculumData],
    );

    const coreCurriculumSelectOptions = useMemo(
        () => mapCoreCurriculumListToSelectOptionsList(parsedCoreCurriculumData),
        [parsedCoreCurriculumData],
    );

    return (
        <>
            <ModalTreePickerElement
                title={journalTexts.journalDetails.groupJournal.topics.coreCurriculum}
                open={isModalOpen}
                onCancel={closeModal}
                onSave={onSaveHandler}
                treeData={mappedCoreCurriculumDataForTree}
                initialValue={curriculumsSelectValue}
                isLoading={isCoreCurriculumDataFetching}
                width={800}
                showCheckAll={false}
                showSearchBar={false}
            />
            <AppFormItem
                label={journalTexts.journalDetails.groupJournal.topics.coreCurriculum}
                name={formFields.coreCurriculums}
            >
                <AppCustomSelect
                    mode="multiple"
                    value={curriculumsSelectValue}
                    suffixIcon={<IconPlus size={20} />}
                    onClick={openModal}
                    onClear={onClearHandler}
                    loading={isCoreCurriculumDataFetching}
                    disabled={!lessonDate}
                    options={coreCurriculumSelectOptions}
                    placeholder="Wybierz podstawę programową"
                />
            </AppFormItem>
        </>
    );
};
